import { API_BASE } from './api';

// Browser side of comment notifications: permission, subscription, and which
// events this device follows (remembered locally so the button state persists).

const FOLLOWED_KEY = 'frolik_followed_events';

function readFollowed() {
  try {
    return new Set(JSON.parse(localStorage.getItem(FOLLOWED_KEY) || '[]'));
  } catch (error) {
    return new Set();
  }
}

function writeFollowed(set) {
  try {
    localStorage.setItem(FOLLOWED_KEY, JSON.stringify([...set]));
  } catch (error) {
    // Storage blocked: the button just won't remember its state.
  }
}

export function isFollowing(slug) {
  return readFollowed().has(slug);
}

function isIos() {
  const ua = navigator.userAgent || '';
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

function isStandalone() {
  return Boolean(
    (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      navigator.standalone
  );
}

// 'supported' | 'ios-needs-install' | 'unsupported'
export function pushSupport() {
  if (typeof window === 'undefined') return 'unsupported';
  if (isIos() && !isStandalone()) return 'ios-needs-install';
  if ('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window) {
    return 'supported';
  }
  return 'unsupported';
}

export function notificationPermission() {
  return typeof Notification === 'undefined' ? 'default' : Notification.permission;
}

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = window.atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
}

async function getSubscription({ create }) {
  const registration = create
    ? await navigator.serviceWorker.register('/sw.js')
    : await navigator.serviceWorker.getRegistration('/');
  if (!registration) return null;

  let subscription = await registration.pushManager.getSubscription();
  if (subscription || !create) return subscription;

  await navigator.serviceWorker.ready;
  const res = await fetch(`${API_BASE}/api/push/public-key`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.publicKey) {
    throw new Error('Notifications aren’t available right now.');
  }

  subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(data.publicKey),
  });
  return subscription;
}

// This device's push endpoint, if it has one. Never prompts. Sent with comments
// so the commenter isn't notified about their own message.
export async function currentPushEndpoint() {
  try {
    if (pushSupport() !== 'supported') return null;
    const subscription = await getSubscription({ create: false });
    return subscription ? subscription.endpoint : null;
  } catch (error) {
    return null;
  }
}

// Must be called straight from a tap: the permission prompt needs that.
export async function followEvent(slug, token) {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error(
      permission === 'denied'
        ? 'Notifications are blocked for Frolik in your browser settings.'
        : 'Notifications weren’t turned on.'
    );
  }

  const subscription = await getSubscription({ create: true });
  const res = await fetch(`${API_BASE}/api/events/${encodeURIComponent(slug)}/follow`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ subscription: subscription.toJSON() }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Couldn’t turn on notifications.');

  const followed = readFollowed();
  followed.add(slug);
  writeFollowed(followed);
}

export async function unfollowEvent(slug) {
  const endpoint = await currentPushEndpoint();
  if (endpoint) {
    await fetch(`${API_BASE}/api/events/${encodeURIComponent(slug)}/follow`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    });
  }
  const followed = readFollowed();
  followed.delete(slug);
  writeFollowed(followed);
}
